/**
 * Skeleton loader for ExploreContentViewWorkoutTemplate
 */
import * as React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../../../Theme/Theme';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 8px;
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 24px auto;
  padding: 0 24px 48px;

  @media (max-width: 768px) {
    padding: 8px 16px 32px;
  }
`;

const HeroSkeleton = styled(SkeletonBase)`
  position: relative;
  height: 400px;
  border-radius: 20px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    height: 480px;
  }
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px;
`;

const PlayButtonSkeleton = styled(SkeletonBase)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.2) 25%, rgba(0, 0, 0, 0.15) 50%, rgba(0, 0, 0, 0.2) 75%);
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string; light?: boolean }>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '14px'};
  background: ${({ light }) =>
    light
      ? 'linear-gradient(90deg, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.3) 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 48px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 992px) {
    order: -1;
  }
`;

const DescriptionSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const ButtonSkeleton = styled(SkeletonBase)`
  height: 56px;
  border-radius: 12px;
`;

const InfoCardSkeleton = styled.div`
  background: ${Theme.Colors.white};
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 16px;
  padding: 20px;
`;

const PhaseCardSkeleton = styled.div`
  background: ${Theme.Colors.white};
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
`;

const PhaseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const ExerciseRowSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const ExerciseImageSkeleton = styled(SkeletonBase)`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  flex-shrink: 0;
`;

const ExerciseInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ExploreContentViewWorkoutTemplateSkeleton: React.FC = () => (
  <PageContainer>
    <HeroSkeleton>
      <PlayButtonSkeleton />
      <HeroContent>
        <SkeletonText width="60%" height="32px" light style={{ marginBottom: 12 }} />
        <MetaRow>
          <SkeletonText width="80px" height="16px" light />
          <SkeletonText width="100px" height="16px" light />
        </MetaRow>
      </HeroContent>
    </HeroSkeleton>

    <ContentSection>
      <MainContent>
        <DescriptionSkeleton>
          <SkeletonText width="100%" height="14px" />
          <SkeletonText width="95%" height="14px" />
          <SkeletonText width="70%" height="14px" />
        </DescriptionSkeleton>

        {/* Phase cards skeleton */}
        {Array.from({ length: 3 }).map((_, phaseIndex) => (
          <PhaseCardSkeleton key={phaseIndex}>
            <PhaseHeader>
              <SkeletonText width="24px" height="24px" style={{ borderRadius: '50%' }} />
              <SkeletonText width="120px" height="18px" />
            </PhaseHeader>
            {Array.from({ length: phaseIndex === 1 ? 4 : 2 }).map((_, i) => (
              <ExerciseRowSkeleton key={i}>
                <ExerciseImageSkeleton />
                <ExerciseInfo>
                  <SkeletonText width="70%" height="16px" />
                  <SkeletonText width="40%" height="12px" />
                </ExerciseInfo>
                <SkeletonText width="60px" height="14px" />
              </ExerciseRowSkeleton>
            ))}
          </PhaseCardSkeleton>
        ))}
      </MainContent>

      <Sidebar>
        <ButtonSkeleton />
        <ButtonSkeleton style={{ height: 44 }} />

        <InfoCardSkeleton>
          <SkeletonText width="80px" height="12px" style={{ marginBottom: 12 }} />
          <SkeletonText width="60px" height="28px" />
        </InfoCardSkeleton>

        <InfoCardSkeleton>
          <SkeletonText width="80px" height="12px" style={{ marginBottom: 12 }} />
          <SkeletonText width="40px" height="28px" />
        </InfoCardSkeleton>
      </Sidebar>
    </ContentSection>
  </PageContainer>
);
